/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * sharePayload.js
 *
 * PURE helpers behind "Share to Happy" — the OS share-sheet target that lets a
 * user send text, links, photos, videos, PDFs and any other file from another
 * app straight into a Happy feature (e-mail, Team Chat, work order, CRM lead /
 * customer, project, task).
 *
 * The native side (`expo-share-intent`: Android SEND / SEND_MULTIPLE intent
 * filters + the iOS share extension) hands over a raw `shareIntent` object; this
 * module turns it into ONE normalized payload and answers every "can this go
 * there?" question the share screen asks. No React Native import lives here on
 * purpose, so the whole decision layer is unit-tested without mocks
 * (`app/tests/sharePayload.test.js`).
 *
 * Server limits mirrored here (keep in sync with the backend):
 *  - e-mail: 10 MB inline base64 total — `MailComposeScreen.MAX_ATTACHMENT_BYTES`;
 *  - Team Chat: 50 MB per file, any type — `services/teamChatApi.CHAT_ATTACHMENT_MAX_BYTES`;
 *  - work order + generic attachments API: 40 MB per file and the MIME allowlist
 *    of `HappyPOS-backend/src/api/utils/upload.middleware.js#ATTACHMENT_MIME_ALLOWLIST`.
 *
 * @module helpers/share/sharePayload
 */

/** Kinds a shared file is grouped into (drives icons, previews and routing). */
export const SHARE_KINDS = Object.freeze({
    IMAGE: 'image',
    VIDEO: 'video',
    AUDIO: 'audio',
    PDF: 'pdf',
    DOCUMENT: 'document',
    FILE: 'file',
});

/** Every in-app destination the share screen can route a payload to. */
export const SHARE_DESTINATIONS = Object.freeze({
    EMAIL: 'email',
    CHAT: 'chat',
    WORK_ORDER: 'workOrder',
    LEAD: 'lead',
    CUSTOMER: 'customer',
    PROJECT: 'project',
    TASK: 'task',
});

/** Display order of the destination tiles. */
export const SHARE_DESTINATION_ORDER = Object.freeze([
    SHARE_DESTINATIONS.CHAT,
    SHARE_DESTINATIONS.EMAIL,
    SHARE_DESTINATIONS.WORK_ORDER,
    SHARE_DESTINATIONS.LEAD,
    SHARE_DESTINATIONS.CUSTOMER,
    SHARE_DESTINATIONS.PROJECT,
    SHARE_DESTINATIONS.TASK,
]);

/** Aggregate inline-attachment cap of the mail composer (base64 in the JSON body). */
export const MAIL_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

/** Per-file cap of a Team Chat attachment. */
export const CHAT_ATTACHMENT_MAX_BYTES = 50 * 1024 * 1024;

/** Per-file cap of the work-order / generic attachments upload (multer limit). */
export const DOCUMENT_UPLOAD_MAX_BYTES = 40 * 1024 * 1024;

/**
 * MIME types the work-order and generic attachments endpoints accept. Mirrors
 * the backend allowlist; anything else is answered with 415, so the share screen
 * filters it out up-front and tells the user instead of failing mid-upload.
 * @type {ReadonlyArray<string>}
 */
export const DOCUMENT_UPLOAD_MIME_ALLOWLIST = Object.freeze([
    'application/pdf',
    'image/png',
    'image/jpg',
    'image/jpeg',
    'image/heic',
    'image/heif',
    'image/webp',
    'image/gif',
    'image/bmp',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
    'application/csv',
    'text/plain',
]);

/** Destinations that only take files (text alone has nowhere to go there). */
const FILE_ONLY_DESTINATIONS = new Set([
    SHARE_DESTINATIONS.WORK_ORDER,
    SHARE_DESTINATIONS.LEAD,
    SHARE_DESTINATIONS.CUSTOMER,
    SHARE_DESTINATIONS.PROJECT,
    SHARE_DESTINATIONS.TASK,
]);

/** Extension → MIME map for files whose sender declared no (or a generic) type. */
const EXTENSION_MIME = Object.freeze({
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    heic: 'image/heic',
    heif: 'image/heif',
    bmp: 'image/bmp',
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    csv: 'text/csv',
    txt: 'text/plain',
    zip: 'application/zip',
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    m4v: 'video/x-m4v',
    mp3: 'audio/mpeg',
    m4a: 'audio/mp4',
    wav: 'audio/wav',
    ogg: 'audio/ogg',
});

/** MIME → extension, used to name an anonymous shared file. */
const MIME_EXTENSION = Object.freeze(Object.entries(EXTENSION_MIME).reduce((acc, [ext, mime]) => {
    if (!acc[mime]) {
        acc[mime] = ext;
    }

    return acc;
}, {}));

const GENERIC_MIMES = new Set(['', 'application/octet-stream', 'binary/octet-stream', '*/*']);

const DOCUMENT_MIME_RE = /(msword|wordprocessingml|ms-excel|spreadsheetml|ms-powerpoint|presentationml|opendocument|text\/|csv|rtf)/i;

/**
 * Lower-cased extension of a file name (without the dot), or ''.
 * @param {string} name File name or path.
 * @returns {string}
 */
export const extensionOf = (name) => {
    const match = /\.([a-z0-9]{1,8})$/i.exec(String(name || '').split('?')[0]);

    return match ? match[1].toLowerCase() : '';
};

/**
 * Guess a MIME type from a file name.
 * @param {string} name File name or path.
 * @returns {string} The MIME type, or `application/octet-stream`.
 */
export const guessMimeFromName = (name) => EXTENSION_MIME[extensionOf(name)] || 'application/octet-stream';

/**
 * Resolve the effective MIME type of a shared file: the sender's declaration
 * when it is specific, otherwise a guess from the file name.
 * @param {string} [mimeType] Declared MIME type.
 * @param {string} [name] File name.
 * @returns {string}
 */
export const resolveMimeType = (mimeType, name) => {
    const declared = String(mimeType || '').trim().toLowerCase();

    if (!GENERIC_MIMES.has(declared) && declared.includes('/') && !declared.endsWith('/*')) {
        return declared;
    }

    return guessMimeFromName(name);
};

/**
 * Classify a file into one of {@link SHARE_KINDS}.
 * @param {string} mimeType Effective MIME type.
 * @returns {string}
 */
export const classifyMime = (mimeType) => {
    const mime = String(mimeType || '').toLowerCase();

    if (mime.startsWith('image/')) {
        return SHARE_KINDS.IMAGE;
    }
    if (mime.startsWith('video/')) {
        return SHARE_KINDS.VIDEO;
    }
    if (mime.startsWith('audio/')) {
        return SHARE_KINDS.AUDIO;
    }
    if (mime === 'application/pdf') {
        return SHARE_KINDS.PDF;
    }
    if (DOCUMENT_MIME_RE.test(mime)) {
        return SHARE_KINDS.DOCUMENT;
    }

    return SHARE_KINDS.FILE;
};

/**
 * Turn a native path into a `file://` URI the RN uploaders accept. Content and
 * already-schemed URIs pass through untouched.
 * @param {string} path Native path or URI.
 * @returns {string}
 */
export const ensureFileUri = (path) => {
    const value = String(path || '').trim();

    if (!value) {
        return '';
    }
    if (/^[a-z][a-z0-9+.-]*:/i.test(value)) {
        return value;
    }

    return `file://${value.startsWith('/') ? '' : '/'}${value}`;
};

/**
 * A human-readable file name for a shared file: the sender's own name, else the
 * last path segment, else a stamped `shared-<n>.<ext>`. Always carries an
 * extension when the MIME type implies one (servers and mail clients key on it).
 * @param {{fileName?: string, path?: string}} file Raw shared file.
 * @param {string} mimeType Effective MIME type.
 * @param {number} index Position in the share (0-based).
 * @returns {string}
 */
export const deriveFileName = (file, mimeType, index) => {
    const own = String(file?.fileName || '').trim();
    const fromPath = decodeSafe(String(file?.path || '').split('?')[0].split('/').pop() || '');
    const base = own || fromPath || `shared-${index + 1}`;

    if (extensionOf(base)) {
        return base;
    }

    const ext = MIME_EXTENSION[mimeType];

    return ext ? `${base}.${ext}` : base;
};

const decodeSafe = (value) => {
    try {
        return decodeURIComponent(value);
    } catch (_error) {
        return value;
    }
};

const toFiniteNumber = (value) => {
    const number = Number(value);

    return Number.isFinite(number) && number > 0 ? number : 0;
};

/**
 * @typedef {Object} ShareItem
 * @property {string} id Stable id within the payload.
 * @property {string} uri `file://` (or `content://`) URI of the local copy.
 * @property {string} name Human-readable file name with extension.
 * @property {string} mimeType Effective MIME type.
 * @property {string} kind One of {@link SHARE_KINDS}.
 * @property {number} size Size in bytes (0 when the sender did not say).
 * @property {number} [width] Image / video width.
 * @property {number} [height] Image / video height.
 */

/**
 * @typedef {Object} SharePayload
 * @property {string} id Unique id of this share.
 * @property {number} receivedAt Epoch ms the share arrived.
 * @property {string} text Shared plain text ('' when none).
 * @property {string} webUrl Link found in the share ('' when none).
 * @property {string} title Page / content title supplied by the sender ('' when none).
 * @property {ShareItem[]} items Shared files.
 */

/**
 * Normalize the raw `expo-share-intent` value into a {@link SharePayload}.
 *
 * @param {{text?: ?string, webUrl?: ?string, files?: ?Array<object>, meta?: ?object}} raw
 *   The `shareIntent` object from `useShareIntent()`.
 * @param {{now?: number}} [options] Clock override (tests).
 * @returns {?SharePayload} `null` when the share carries nothing usable.
 */
export const normalizeShareIntent = (raw, options = {}) => {
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const now = Number.isFinite(options.now) ? options.now : Date.now();
    const files = Array.isArray(raw.files) ? raw.files : [];
    const items = files
        .filter((file) => file && (file.path || file.uri))
        .map((file, index) => {
            const uri = ensureFileUri(file.path || file.uri);
            const mimeType = resolveMimeType(file.mimeType || file.type, file.fileName || uri);
            const item = {
                id: `item-${index + 1}`,
                uri,
                name: deriveFileName({fileName: file.fileName, path: uri}, mimeType, index),
                mimeType,
                kind: classifyMime(mimeType),
                size: toFiniteNumber(file.size),
            };

            if (toFiniteNumber(file.width) && toFiniteNumber(file.height)) {
                item.width = toFiniteNumber(file.width);
                item.height = toFiniteNumber(file.height);
            }

            return item;
        });
    const text = String(raw.text || '').trim();
    const webUrl = String(raw.webUrl || '').trim();
    const title = String(raw.meta?.title || '').trim();

    if (!items.length && !text && !webUrl) {
        return null;
    }

    return {
        id: `share-${now}-${Math.random().toString(36).slice(2, 8)}`,
        receivedAt: now,
        text,
        webUrl,
        title,
        items,
    };
};

/**
 * Whether a file passes the work-order / generic attachments allowlist.
 * @param {ShareItem} item Shared file.
 * @returns {boolean}
 */
export const isDocumentUploadable = (item) => DOCUMENT_UPLOAD_MIME_ALLOWLIST.includes(String(item?.mimeType || '').toLowerCase());

/**
 * Split the payload's files into what a destination accepts and what it would
 * refuse (with the reason), so the screen can say so BEFORE uploading.
 *
 * @param {?SharePayload} payload Normalized share.
 * @param {string} destination One of {@link SHARE_DESTINATIONS}.
 * @returns {{accepted: ShareItem[], rejected: Array<{item: ShareItem, reason: ('type'|'size')}>}}
 */
export const partitionItemsForDestination = (payload, destination) => {
    const items = Array.isArray(payload?.items) ? payload.items : [];
    const accepted = [];
    const rejected = [];

    if (destination === SHARE_DESTINATIONS.EMAIL) {
        let total = 0;

        items.forEach((item) => {
            if (total + item.size > MAIL_ATTACHMENT_MAX_BYTES) {
                rejected.push({item, reason: 'size'});

                return;
            }
            total += item.size;
            accepted.push(item);
        });

        return {accepted, rejected};
    }

    items.forEach((item) => {
        if (destination === SHARE_DESTINATIONS.CHAT) {
            if (item.size > CHAT_ATTACHMENT_MAX_BYTES) {
                rejected.push({item, reason: 'size'});
            } else {
                accepted.push(item);
            }

            return;
        }
        if (!isDocumentUploadable(item)) {
            rejected.push({item, reason: 'type'});
        } else if (item.size > DOCUMENT_UPLOAD_MAX_BYTES) {
            rejected.push({item, reason: 'size'});
        } else {
            accepted.push(item);
        }
    });

    return {accepted, rejected};
};

/**
 * Whether the shared text already contains the shared link (so it is not
 * repeated twice in a message).
 * @param {?SharePayload} payload Normalized share.
 * @returns {boolean}
 */
const textCarriesUrl = (payload) => !!payload?.webUrl && String(payload?.text || '').includes(payload.webUrl);

/**
 * The plain-text body of a share: the user's own note first, then the sender's
 * title, text and link (de-duplicated).
 * @param {?SharePayload} payload Normalized share.
 * @param {string} [note] The user's note typed on the share screen.
 * @returns {string}
 */
export const buildShareText = (payload, note = '') => {
    const parts = [String(note || '').trim()];

    if (payload?.title && payload.title !== payload.text) {
        parts.push(payload.title);
    }
    if (payload?.text) {
        parts.push(payload.text);
    }
    if (payload?.webUrl && !textCarriesUrl(payload)) {
        parts.push(payload.webUrl);
    }

    return parts.filter(Boolean).join('\n\n');
};

const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const URL_RE = /(https?:\/\/[^\s<]+)/g;

/**
 * Escape a paragraph and turn bare http(s) links into anchors.
 * @param {string} paragraph Plain text.
 * @returns {string} HTML.
 */
const paragraphToHtml = (paragraph) => escapeHtml(paragraph)
    .replace(URL_RE, (url) => `<a href="${url}">${url}</a>`)
    .replace(/\n/g, '<br>');

/**
 * The HTML body of an e-mail built from a share (same content as
 * {@link buildShareText}, escaped, links clickable).
 * @param {?SharePayload} payload Normalized share.
 * @param {string} [note] The user's note.
 * @returns {string} HTML ('' when there is no text at all).
 */
export const buildShareEmailHtml = (payload, note = '') => buildShareText(payload, note)
    .split(/\n{2,}/)
    .filter(Boolean)
    .map((paragraph) => `<p>${paragraphToHtml(paragraph)}</p>`)
    .join('');

/**
 * A sensible e-mail subject for a share: the sender's title, else the first
 * file name, else the first line of the text — capped at 120 characters.
 * @param {?SharePayload} payload Normalized share.
 * @returns {string}
 */
export const buildShareSubject = (payload) => {
    const firstLine = String(payload?.text || '').split('\n').map((line) => line.trim()).find(Boolean) || '';
    const candidate = payload?.title || payload?.items?.[0]?.name || firstLine || payload?.webUrl || '';
    const subject = String(candidate).trim();

    return subject.length > 120 ? `${subject.slice(0, 119)}…` : subject;
};

/**
 * Counts used by the screen header and the destination subtitles.
 * @param {?SharePayload} payload Normalized share.
 * @returns {{fileCount: number, imageCount: number, totalBytes: number, hasText: boolean}}
 */
export const summarizeSharePayload = (payload) => {
    const items = Array.isArray(payload?.items) ? payload.items : [];

    return {
        fileCount: items.length,
        imageCount: items.filter((item) => item.kind === SHARE_KINDS.IMAGE).length,
        totalBytes: items.reduce((sum, item) => sum + (item.size || 0), 0),
        hasText: !!(payload?.text || payload?.webUrl),
    };
};

/**
 * @typedef {Object} ShareCapabilities
 * @property {boolean} mail Mail feature on AND a mailbox connected.
 * @property {boolean} [mailFeature] Mail feature on (used to word the reason when no mailbox is connected).
 * @property {boolean} chat Team Chat visible for this user / store.
 * @property {boolean} service Service module (work orders) in this build.
 * @property {boolean} crm Management or Sales module (leads, customers).
 * @property {boolean} projects Projects / tasks reachable (management or service module).
 */

/**
 * Resolve every destination tile for a payload: whether it can be used and,
 * when not, the i18n reason key suffix (`share_reason_<reason>`).
 *
 * @param {?SharePayload} payload Normalized share.
 * @param {ShareCapabilities} capabilities What this user / build can reach.
 * @returns {Array<{id: string, enabled: boolean, reason: ?string, accepted: number, rejected: number}>}
 */
export const resolveShareDestinations = (payload, capabilities = {}) => {
    const caps = capabilities || {};
    const available = {
        [SHARE_DESTINATIONS.EMAIL]: caps.mail ? null : (caps.mailFeature ? 'no_mailbox' : 'unavailable'),
        [SHARE_DESTINATIONS.CHAT]: caps.chat ? null : 'unavailable',
        [SHARE_DESTINATIONS.WORK_ORDER]: caps.service ? null : 'unavailable',
        [SHARE_DESTINATIONS.LEAD]: caps.crm ? null : 'unavailable',
        [SHARE_DESTINATIONS.CUSTOMER]: caps.crm ? null : 'unavailable',
        [SHARE_DESTINATIONS.PROJECT]: caps.projects ? null : 'unavailable',
        [SHARE_DESTINATIONS.TASK]: caps.projects ? null : 'unavailable',
    };
    const fileCount = Array.isArray(payload?.items) ? payload.items.length : 0;

    return SHARE_DESTINATION_ORDER.map((id) => {
        const {accepted, rejected} = partitionItemsForDestination(payload, id);
        let reason = available[id];

        if (!reason && FILE_ONLY_DESTINATIONS.has(id)) {
            if (!fileCount) {
                reason = 'needs_files';
            } else if (!accepted.length) {
                reason = 'unsupported_files';
            }
        }
        if (!reason && id === SHARE_DESTINATIONS.EMAIL && fileCount && !accepted.length && !summarizeSharePayload(payload).hasText) {
            reason = 'too_large';
        }

        return {id, enabled: !reason, reason: reason || null, accepted: accepted.length, rejected: rejected.length};
    });
};

/**
 * Format a byte count for display (`1.4 MB`).
 * @param {number} bytes Byte count.
 * @returns {string} '' for an unknown (0) size.
 */
export const formatShareBytes = (bytes) => {
    const value = Number(bytes) || 0;

    if (value <= 0) {
        return '';
    }
    if (value < 1024) {
        return `${value} B`;
    }
    if (value < 1024 * 1024) {
        return `${Math.round(value / 1024)} KB`;
    }

    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};
